import React, { useState, useEffect } from 'react';
import { FaStore, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaGasPump, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminStationModal from '../../components/admin/AdminStationModal';
import AdminFuelModal from '../../components/admin/AdminFuelModal';
import { stationService } from '../../services/refuelService';
import { FuelStation } from '../../types';
import { formatOpeningHours } from '../../utils/openingHours';

const AdminStations: React.FC = () => {
  const [stations, setStations] = useState<FuelStation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para modal de posto
  const [showStationModal, setShowStationModal] = useState(false);
  const [editingStation, setEditingStation] = useState<FuelStation | null>(null);
  const [isLoadingEditStation, setIsLoadingEditStation] = useState(false);
  
  // Estados para modal de combustíveis
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [selectedStation, setSelectedStation] = useState<FuelStation | null>(null);

  useEffect(() => {
    loadStations();
  }, []);

  const loadStations = async () => {
    setIsLoading(true);
    try {
      const response = await stationService.getAllStations();
      if (response.success && response.data) {
        setStations(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar postos:', error);
      toast.error('Erro ao carregar postos');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleOpenCreateModal = () => {
    setEditingStation(null);
    setShowStationModal(true);
  };
  
  const handleOpenEditModal = async (station: FuelStation) => {
    setIsLoadingEditStation(true);
    try {
      const response = await stationService.getStationById(station.id);
      if (response.success && response.data) {
        setEditingStation(response.data);
        setShowStationModal(true);
        return;
      }

      toast.error(response.message || 'Erro ao carregar dados do posto');
    } catch (error) {
      console.error('Erro ao carregar posto para edição:', error);
      toast.error('Erro ao carregar dados do posto');
    } finally {
      setIsLoadingEditStation(false);
    }
  };
  
  const handleOpenFuelModal = (station: FuelStation) => {
    setSelectedStation(station);
    setShowFuelModal(true);
  };
  
  const handleStationModalClose = () => {
    setShowStationModal(false);
    setEditingStation(null);
  };
  
  const handleFuelModalClose = () => {
    setShowFuelModal(false);
    setSelectedStation(null);
  };
  
  const handleSuccess = () => {
    loadStations();
  };

  const handleDeleteStation = async (station: FuelStation) => {
    const confirmed = window.confirm(`Deseja realmente excluir o posto ${station.nomePosto}?`);

    if (!confirmed) {
      return;
    }

    try {
      const response = await stationService.deleteStation(station.id);
      if (response.success) {
        toast.success(response.message || 'Posto excluído com sucesso');
        loadStations();
      } else {
        toast.error(response.message || 'Erro ao excluir posto');
      }
    } catch (error: any) {
      console.error('Erro ao excluir posto:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir posto');
    }
  };

  const filteredStations = stations.filter(station =>
    station.nomePosto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    station.cnpj?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    station.cidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    station.parceiroNome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0f1419]">
        <AdminHeader />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-70px)] text-[#888]">
          <div className="w-12 h-12 border-4 border-[#089453]/10 border-t-[#089453] rounded-full animate-spin"></div>
          <p className="mt-4 text-lg">Carregando postos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1419]">
      <AdminHeader />

      <main className="max-w-[1400px] mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <FaStore className="text-[#089453]" />
            Gerenciamento de Postos
          </h1>
          <p className="text-[#888]">
            Visualize e gerencie todos os postos parceiros do sistema
          </p>
        </div>

        {/* Search and Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,1fr)] gap-6 items-stretch mb-8">
          <div className="flex items-stretch">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar por nome, CNPJ ou cidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-full min-h-[64px] pl-12 pr-4 bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-xl text-white text-base transition-all duration-200 placeholder:text-[#666] focus:outline-none focus:border-[#089453] focus:bg-[#089453]/5"
              />
              <FaStore className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-2xl p-6 hover:border-[#089453]/30 transition-all duration-300 flex flex-col items-center justify-center">
            <h3 className="text-3xl font-bold text-white mb-1">{stations.length}</h3>
            <p className="text-[#888] text-sm m-0">Total de Postos</p>
          </div>
        </div>

        {/* Botão de Cadastrar Posto */}
        <div className="mb-6">
          <button
            onClick={handleOpenCreateModal}
            className="px-6 py-3 bg-gradient-to-r from-[#089453] to-[#84cc34] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-[#089453]/30 transition-all duration-300 flex items-center gap-2"
          >
            <FaPlus />
            Cadastrar Novo Posto
          </button>
        </div>

        {/* Stations Grid */}
        {filteredStations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStations.map((station) => (
              <div
                key={station.id}
                className="bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-2xl p-6 hover:border-[#089453]/30 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#089453] to-[#84cc34] rounded-xl flex items-center justify-center">
                      <FaStore className="text-white text-xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{station.nomePosto}</h3>
                      {station.cnpj && (
                        <p className="text-xs text-[#888]">CNPJ: {station.cnpj}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {station.cidade && (
                    <div className="flex items-center gap-2 text-sm text-[#888]">
                      <FaMapMarkerAlt className="text-[#089453]" />
                      <span>{station.cidade}, {station.estado}</span>
                    </div>
                  )}
                  {station.telefone && (
                    <div className="flex items-center gap-2 text-sm text-[#888]">
                      <FaPhone className="text-[#089453]" />
                      <span>{station.telefone}</span>
                    </div>
                  )}
                  {station.email && (
                    <div className="flex items-center gap-2 text-sm text-[#888]">
                      <FaEnvelope className="text-[#089453]" />
                      <span className="truncate">{station.email}</span>
                    </div>
                  )}
                  {(formatOpeningHours(station.aberto24h, station.horarios) || station.horarioFuncionamento) && (
                    <div className="flex items-center gap-2 text-sm text-[#888]">
                      <FaClock className="text-[#089453]" />
                      <span>{formatOpeningHours(station.aberto24h, station.horarios) || station.horarioFuncionamento}</span>
                    </div>
                  )}
                </div>

                {station.combustiveis && station.combustiveis.length > 0 && (
                  <div className="pt-4 border-t border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <FaGasPump className="text-[#089453] text-sm" />
                      <span className="text-xs text-[#888] font-medium">Combustíveis:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {station.combustiveis.map((fuel) => (
                        <span
                          key={fuel.id}
                          className="px-2 py-1 bg-[#089453]/10 text-[#84cc34] rounded-lg text-xs font-medium"
                        >
                          {fuel.tipoCombustivel.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {station.combustiveis?.some((fuel) => (fuel.percentualCashback || 0) > 0 || (fuel.descontoPorLitro || 0) > 0) && (
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#888]">Benefícios:</span>
                      <span className="text-sm font-bold text-[#84cc34]">
                        Por combustível
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-xs text-[#666]">
                    Parceiro: <span className="text-[#888]">{station.parceiroNome || station.parceiro?.fullName || 'N/A'}</span>
                  </p>
                </div>

                {/* Botões de Ação */}
                <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    onClick={() => handleOpenEditModal(station)}
                    disabled={isLoadingEditStation}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#089453] to-[#84cc34] text-white rounded-lg hover:shadow-lg hover:shadow-[#089453]/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <FaEdit />
                    Editar
                  </button>
                  <button
                    onClick={() => handleOpenFuelModal(station)}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#0066cc] to-[#0099ff] text-white rounded-lg hover:shadow-lg hover:shadow-[#0066cc]/30 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaGasPump />
                    Combustíveis
                  </button>
                  <button
                    onClick={() => handleDeleteStation(station)}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#cc0000] to-[#ff3333] text-white rounded-lg hover:shadow-lg hover:shadow-[#cc0000]/30 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaTrash />
                    Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-2xl p-12 text-center">
            <FaStore className="text-6xl text-[#888] mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-white mb-2">
              {searchTerm ? 'Nenhum posto encontrado' : 'Nenhum posto cadastrado'}
            </h3>
            <p className="text-[#888]">
              {searchTerm
                ? 'Tente buscar com outros termos'
                : 'Os postos cadastrados aparecerão aqui'}
            </p>
          </div>
        )}
      </main>

      {/* Modais */}
      <AdminStationModal
        isOpen={showStationModal}
        onClose={handleStationModalClose}
        onSuccess={handleSuccess}
        station={editingStation}
      />

      {selectedStation && (
        <AdminFuelModal
          isOpen={showFuelModal}
          onClose={handleFuelModalClose}
          onSuccess={handleSuccess}
          station={selectedStation}
        />
      )}
    </div>
  );
};

export default AdminStations;

